import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/react';
import { db } from '../../../api/firebase/config';
import SideBar from '../../uiParts/menu/SideBar';
import MetaTitle from '../../utils/MetaTitle';
import CustomLoading from '../../uiParts/form/CustomLoading';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import EmailMessageDetailDialog from './EmailMessageDetailDialog';
import EmailStatusLabel from './EmailStatusLabel';
import { usePageLayout } from '../../../hooks/usePageLayout';
import { useCompanyId } from '../../../hooks/useCompanyId';
import { usePatternList } from '../../../hooks/usePatternList';
import { getFirstResultRefByEmailMessageId } from '../../../api/firebase/firestore/result/read';
import { EmailInbox, EmailMessage } from '../../../types/emailImport';
import {
  FREE_OCR_PATTERN_ID,
  PATTERN_ID_OTHER,
  emailInboxType,
  emailMessageStatus,
} from '../../../constants/enum';
import {
  gray20,
  gray40,
  gray60,
  gray80,
  gray100,
  orange10,
  red50,
  tertiaryButtonBackgroundColor,
  white,
} from '../../../constants/colors';
import {
  boldXxl,
  regularMd,
  regularSm,
  sectionTitle,
  spHeaderTitle,
} from '../../../constants/location';

const styles = {
  headerTitle: {
    ...boldXxl,
    color: gray100,
    '@media screen and (max-width:960px)': {
      ...spHeaderTitle,
    },
  },
  headerButton: {
    height: '44px',
    padding: '0 16px',
    whiteSpace: 'nowrap' as const,
    '& .MuiButton-icon': {
      marginRight: '4px',
    },
  },
  tableSection: {
    backgroundColor: white,
    border: '1px solid' + gray40,
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    margin: '0 32px',
    minHeight: '560px',
    padding: '16px 24px 56px',
    width: 'calc(100% - 64px)',
    '@media screen and (max-width:960px)': {
      border: 'none',
      borderRadius: 0,
      margin: 0,
      width: '100%',
    },
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    width: '100%',
    WebkitOverflowScrolling: 'touch',
  },
  table: {
    minWidth: '960px',
    width: '100%',
    // 列幅を固定配分し、件名列に余りを寄せる（省略表示を確実に効かせるため）
    tableLayout: 'fixed' as const,
  },
  tableHeadRow: {
    height: '48px',
  },
  tableHeadText: {
    ...regularSm,
    color: gray80,
    padding: '0 16px',
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis' as const,
  },
  // 固定長で足りる列は必要最低限の幅にする（余りは件名列へ）
  colStatus: { width: '100px' },
  colFrom: { width: '190px' },
  colAttach: { width: '64px' },
  colAddrName: { width: '150px' },
  colPattern: { width: '150px' },
  colDate: { width: '144px' },
  tableDataRow: {
    height: '48px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: gray20,
    },
  },
  // ×ボタンで戻った直後、対象行を一時的に強調表示する
  tableDataRowHighlight: {
    backgroundColor: orange10,
    '&:hover': {
      backgroundColor: orange10,
    },
  },
  tableDataText: {
    ...regularSm,
    color: gray100,
    padding: '0 16px',
    whiteSpace: 'nowrap' as const,
  },
  // 固定幅の列で内容がはみ出す場合に省略表示する（table-layout:fixed 用の内側ブロック）
  ellipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const,
  },
  // 件名セル: 件名＋本文抜粋（Gmail風）を1行で省略表示。余った幅を件名列が吸収する
  subjectInner: {
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const,
  },
  // 件名に続けて薄く表示する本文抜粋
  snippetText: {
    color: gray80,
  },
  errorText: {
    ...regularSm,
    color: red50,
    padding: '16px',
  },
  emptyWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    padding: '64px 16px',
    textAlign: 'center' as const,
  },
  emptyIcon: {
    color: gray60,
    height: '56px',
    marginBottom: '24px',
    width: '56px',
  },
  emptyTitle: {
    ...sectionTitle,
    marginBottom: '8px',
  },
  emptyDescription: {
    ...regularMd,
    color: gray80,
  },
};

const EmailMessageListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const { companyId } = useCompanyId();
  const { patterns } = usePatternList();

  const [messages, setMessages] = useState<EmailMessage[]>([]);
  const [inboxes, setInboxes] = useState<EmailInbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // 詳細モーダル。閉じるアニメーションのため open と表示中メッセージを分離して保持する
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<EmailMessage | null>(null);
  // 表示中メッセージの先頭結果が「該当なし(other)」か。true の間は結果ページを開けないため、
  // 詳細モーダルの「OCR結果を見る」を非活性にする（#3154）。
  const [isOtherResult, setIsOtherResult] = useState(false);

  // ×ボタンで戻った際に対象行へスクロール・強調するための参照と状態
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const [highlightId, setHighlightId] = useState<string | null>(null);

  // アドレス設定ダイアログからの操作結果通知（スナックバーはダイアログの外＝ここで表示）
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarInfoText, setSnackbarInfoText] = useState('');
  const [snackbarErrorText, setSnackbarErrorText] = useState('');
  const handleNotify = (message: string, isError = false): void => {
    setSnackbarInfoText(isError ? '' : message);
    setSnackbarErrorText(isError ? message : '');
    setSnackbarOpen(true);
  };

  const loadMessages = useCallback(async (): Promise<void> => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      // 受信メールと、宛先アドレス（名称・適用パターン解決用）を並行取得する
      const messagesQuery = query(
        collection(db, 'email_messages'),
        where('company_id', '==', companyId),
        orderBy('received_at', 'desc'),
      );
      const inboxesQuery = query(
        collection(db, 'email_inboxes'),
        where('company_id', '==', companyId),
      );
      const [messagesSnap, inboxesSnap] = await Promise.all([
        getDocs(messagesQuery),
        getDocs(inboxesQuery),
      ]);
      setMessages(messagesSnap.docs.map(d => ({ ...(d.data() as EmailMessage), id: d.id })));
      setInboxes(inboxesSnap.docs.map(d => ({ ...(d.data() as EmailInbox), id: d.id })));
    } catch (e) {
      Sentry.captureException(e);
      setError('取り込み履歴の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // inbox_id から宛先アドレス（名称・適用パターン）を引くためのマップ
  const inboxById = useMemo(() => {
    const map = new Map<string, EmailInbox>();
    inboxes.forEach(inbox => map.set(inbox.id, inbox));
    return map;
  }, [inboxes]);

  // 宛先アドレスに設定された読取パターン名。自動仕分アドレスは「自動仕分」
  const patternNameOf = (message: EmailMessage): string => {
    const inbox = inboxById.get(message.inbox_id);
    if (!inbox) return '-';
    if (inbox.inbox_type !== emailInboxType.pattern) return '自動仕分';
    return patterns.find(p => p.patternId === inbox.pattern_id)?.pattern_name ?? '-';
  };

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  // OCR結果画面の×ボタンで戻ってきたとき、focus 指定の行へスクロールして一時的に強調する。
  // 処理後は URL から focus を消し、リロード時の再ハイライトや URL 汚染を防ぐ。
  useEffect(() => {
    if (loading || messages.length === 0) return;
    const focusId = new URLSearchParams(location.search).get('focus');
    if (!focusId) return;
    const row = rowRefs.current[focusId];
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightId(focusId);
    }
    navigate(location.pathname, { replace: true });
  }, [loading, messages, location.search, location.pathname, navigate]);

  // 強調表示は一定時間で解除する
  useEffect(() => {
    if (!highlightId) return;
    const timer = setTimeout(() => setHighlightId(null), 2000);
    return () => clearTimeout(timer);
  }, [highlightId]);

  // 詳細モーダルを開いたとき、先頭結果の仕分けパターンを先読みする。
  // 「該当なし(other)」は結果ページを開けない（無限ローディング）ため、ボタンを非活性にする（#3154）。
  useEffect(() => {
    if (!detailOpen || !selectedMessage || !companyId) {
      setIsOtherResult(false);
      return;
    }
    // ボタン自体が出ない条件（未完了・結果なし）は先読み不要
    if (
      selectedMessage.status !== emailMessageStatus.completed ||
      !(selectedMessage.ocr_job_ids?.length > 0)
    ) {
      setIsOtherResult(false);
      return;
    }
    let cancelled = false;
    setIsOtherResult(false);
    void (async (): Promise<void> => {
      const ref = await getFirstResultRefByEmailMessageId(companyId, selectedMessage.id);
      if (cancelled) return;
      setIsOtherResult(ref?.patternId === PATTERN_ID_OTHER);
    })();
    return () => {
      cancelled = true;
    };
  }, [detailOpen, selectedMessage, companyId]);

  // 「OCR結果を見る」: メッセージ由来の先頭結果を引き、free/pattern の詳細画面へ遷移する
  const handleViewOcrResult = async (message: EmailMessage): Promise<void> => {
    if (!companyId) return;
    const ref = await getFirstResultRefByEmailMessageId(companyId, message.id);
    if (!ref) {
      handleNotify('OCR結果が見つかりませんでした。', true);
      return;
    }
    // 自動仕分アドレス由来は仕分け完了まで pattern_id が未確定（null）。この間は結果ページへ
    // 遷移できないため、壊れたURLへ飛ばさず処理中である旨を通知する（#3040 レビュー②）。
    if (!ref.patternId) {
      handleNotify('仕分け処理中のため、まだ結果を表示できません。しばらくお待ちください。');
      return;
    }
    // 該当なし（自動仕分で対象パターンが無い）は結果ページを表示できないため遷移しない。
    // 通常はボタンを非活性にしているが、解決前クリックのレース対策として二重にガードする（#3154）。
    if (ref.patternId === PATTERN_ID_OTHER) {
      handleNotify('該当するパターンがないため、OCR結果を表示できません。', true);
      return;
    }
    // OCR結果画面の×ボタンで、遷移元（メール取り込み一覧）の対象行へ戻れるよう backTo を渡す。
    // 件数が多くても対象行にスクロール・強調できるよう focus に message.id を載せる。
    const backTo = `/company-settings/email-messages?focus=${encodeURIComponent(message.id)}`;
    const navState = { state: { backTo } };
    if (ref.patternId === FREE_OCR_PATTERN_ID) {
      navigate(`/ai-ocr/free/result/${ref.resultId}`, navState);
    } else {
      navigate(`/ai-ocr/pattern/${ref.patternId}/result/${ref.resultId}`, navState);
    }
  };

  const renderBody = (): React.ReactNode => {
    if (loading) return <CustomLoading loading />;
    if (error) return <Typography sx={styles.errorText}>{error}</Typography>;
    if (messages.length === 0) {
      return (
        <Box sx={styles.emptyWrapper}>
          <MoveToInboxOutlinedIcon sx={styles.emptyIcon} />
          <Typography sx={styles.emptyTitle}>取り込み履歴はまだありません</Typography>
          <Typography sx={styles.emptyDescription}>
            右上の「受信アドレス管理」から専用アドレスを発行し、そのアドレス宛にメールを送りましょう。
          </Typography>
        </Box>
      );
    }
    return (
      <Box sx={styles.tableWrapper}>
        <Table sx={styles.table}>
          <TableHead>
            <TableRow sx={styles.tableHeadRow}>
              <TableCell sx={[styles.tableHeadText, styles.colStatus]}>ステータス</TableCell>
              <TableCell sx={[styles.tableHeadText, styles.colFrom]}>送信元</TableCell>
              <TableCell sx={styles.tableHeadText}>件名</TableCell>
              <TableCell sx={[styles.tableHeadText, styles.colAttach]}>添付</TableCell>
              <TableCell sx={[styles.tableHeadText, styles.colAddrName]}>受信アドレス名</TableCell>
              <TableCell sx={[styles.tableHeadText, styles.colPattern]}>読取パターン</TableCell>
              <TableCell sx={[styles.tableHeadText, styles.colDate]}>受信日時</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messages.map(message => (
              <TableRow
                key={message.id}
                ref={(el: HTMLTableRowElement | null): void => {
                  rowRefs.current[message.id] = el;
                }}
                sx={[
                  styles.tableDataRow,
                  highlightId === message.id && styles.tableDataRowHighlight,
                ]}
                onClick={(): void => {
                  setSelectedMessage(message);
                  setDetailOpen(true);
                }}
              >
                <TableCell sx={styles.tableDataText}>
                  <EmailStatusLabel message={message} showReason={false} reasonTooltip />
                </TableCell>
                <TableCell sx={styles.tableDataText}>
                  <Box sx={styles.ellipsisText}>{message.from}</Box>
                </TableCell>
                <TableCell sx={styles.tableDataText}>
                  <Box sx={styles.subjectInner}>
                    {message.subject || '(件名なし)'}
                    {message.snippet && (
                      <Box component="span" sx={styles.snippetText}>
                        {' '}
                        - {message.snippet}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={styles.tableDataText}>{message.attachment_count} 件</TableCell>
                <TableCell sx={styles.tableDataText}>
                  <Box sx={styles.ellipsisText}>{inboxById.get(message.inbox_id)?.name ?? '-'}</Box>
                </TableCell>
                <TableCell sx={styles.tableDataText}>
                  <Box sx={styles.ellipsisText}>{patternNameOf(message)}</Box>
                </TableCell>
                <TableCell sx={styles.tableDataText}>
                  {dayjs(message.received_at.toDate()).format('YYYY/MM/DD HH:mm')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle customTitle="受信トレイ" />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>
      <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
        <Box
          sx={[
            layoutStyles.header,
            { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
          ]}
        >
          <Typography sx={styles.headerTitle}>受信トレイ</Typography>
          <Button
            onClick={(): void => navigate('/company-settings/email-inboxes')}
            startIcon={<AlternateEmailIcon sx={{ height: '20px', width: '20px' }} />}
            sx={[tertiaryButtonBackgroundColor, styles.headerButton]}
          >
            受信アドレス管理
          </Button>
        </Box>

        <Box sx={styles.tableSection}>{renderBody()}</Box>
      </Box>

      <EmailMessageDetailDialog
        open={detailOpen}
        message={selectedMessage}
        isOtherResult={isOtherResult}
        onClose={(): void => setDetailOpen(false)}
        onViewOcrResult={(message): void => {
          void handleViewOcrResult(message);
        }}
      />

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={snackbarErrorText}
        infoText={snackbarInfoText}
      />
    </Box>
  );
};

export default EmailMessageListPage;
